import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { api } from "../../api/client";
import { qk } from "../../api/queries";
import { Markdown } from "../Markdown";
import { RelativeTime } from "../RelativeTime";
import { ErrorBox, Spinner } from "../Spinner";

/** "edited" marker that opens a popover with the comment's revision history. */
export function EditedMarker({ eventId, editedAt }: { eventId: string; editedAt: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <span className="edited" ref={ref}>
      <button className="link-button muted" onClick={() => setOpen((o) => !o)} title={`Edited ${editedAt}`}>
        edited
      </button>
      {open && <RevisionsPopover eventId={eventId} />}
    </span>
  );
}

function RevisionsPopover({ eventId }: { eventId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: qk.revisions(eventId),
    queryFn: () => api.revisions(eventId),
  });
  return (
    <div className="popover revisions-popover">
      <div className="popover-header">
        <strong>Edit history</strong>
      </div>
      <div className="popover-body">
        {isLoading && <Spinner />}
        {error && <ErrorBox error={error} />}
        {data?.length === 0 && <p className="muted">No earlier revisions.</p>}
        {data?.map((r, i) => (
          <div key={i} className="revision">
            <div className="muted small">
              <RelativeTime iso={r.edited_at} />
            </div>
            <Markdown>{r.body}</Markdown>
          </div>
        ))}
      </div>
    </div>
  );
}
